import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import {
  CloseIcon,
  HamburgerIcon,
} from '@chakra-ui/icons';
import {
  Box,
  Flex,
  IconButton,
  SlideFade,
  useMediaQuery,
} from '@chakra-ui/react';
import { useSize } from '@chakra-ui/react-use-size';

import {
  cockpitGet,
  getCockpitAssetUrl,
} from '../config/cockpit';
import { getCurrentDimension } from '../constants/Utils';
import { useHeader } from '../contexts/useHeader';
import { NavigationLink } from '../models/Header';

import Logo from './Logo';
import NavLink from './NavLink';

interface HeaderProps {
  links: NavigationLink[];
  justLightMode?: boolean;
}

interface CockpitAsset {
  path?: string;
  title?: string;
  altText?: string;
}

interface GlobalData {
  logoWhite?: CockpitAsset;
  logoBlack?: CockpitAsset;
  'logoWhite-square'?: CockpitAsset;
  'logoBlack-square'?: CockpitAsset;
}

const Header = ({
  links,
  justLightMode = false,
}: HeaderProps) => {
  const { setHeaderDimensions } = useHeader();

  const headerRef = useRef(null);
  const dimensions = useSize(headerRef);

  const [isMobile] = useMediaQuery(
    '(max-width: 767px)'
  );

  const [screenSize, setScreenSize] = useState(
    getCurrentDimension()
  );

  const [positionTop, setPositionTop] = useState(0);
  const [showMobileView, setShowMobileView] =
    useState(false);

  const [globalData, setGlobalData] =
    useState<GlobalData | null>(null);

  useEffect(() => {
    cockpitGet<GlobalData>(
      '/api/content/item/Global'
    )
      .then((data) => {
        setGlobalData(data);
      })
      .catch((err: unknown) => {
        // eslint-disable-next-line no-console
        console.error(
          'Failed to load Global header data:',
          err
        );

        setGlobalData(null);
      });
  }, []);

  /*
   * Prefer the square logo fields from Cockpit.
   * If they are empty, fall back to the normal logo fields.
   */
  const whiteLogo = useMemo(() => {
    const asset =
      globalData?.['logoWhite-square'] ||
      globalData?.logoWhite;

    if (!asset?.path) {
      return '';
    }

    return getCockpitAssetUrl(asset.path);
  }, [globalData]);

  const blackLogo = useMemo(() => {
    const asset =
      globalData?.['logoBlack-square'] ||
      globalData?.logoBlack;

    if (!asset?.path) {
      return '';
    }

    return getCockpitAssetUrl(asset.path);
  }, [globalData]);

  const darkMode = useMemo(
    () =>
      justLightMode
        ? false
        : !!dimensions &&
          positionTop <=
            screenSize.height -
              dimensions.height / 2,
    [
      dimensions,
      positionTop,
      screenSize,
      justLightMode,
    ]
  );

  const headerHeight = useMemo(
    () => window.innerHeight,
    []
  );

  const handleScroll = () => {
    setPositionTop(window.scrollY);
  };

  useEffect(() => {
    window.addEventListener(
      'scroll',
      handleScroll,
      {
        passive: true,
      }
    );

    return () => {
      window.removeEventListener(
        'scroll',
        handleScroll
      );
    };
  }, []);

  useEffect(() => {
    const updateDimension = () => {
      setScreenSize(getCurrentDimension());
    };

    window.addEventListener(
      'resize',
      updateDimension
    );

    return () => {
      window.removeEventListener(
        'resize',
        updateDimension
      );
    };
  }, []);

  useEffect(() => {
    if (dimensions) {
      setHeaderDimensions(dimensions);
    }
  }, [dimensions, setHeaderDimensions]);

  const currentLogo = darkMode
    ? whiteLogo
    : blackLogo;

  return (
    <>
      <Flex
        id="header"
        ref={headerRef}
        minWidth="100%"
        justifyContent="center"
        alignItems="center"
        bgGradient={
          darkMode
            ? 'linear-gradient(180deg, rgba(0, 0, 0) 20%, rgba(0, 0, 0, 0.85) 35%, rgba(0, 0, 0, 0.60) 55%, rgba(0, 0, 0, 0.00) 100%)'
            : 'linear-gradient(180deg, rgba(255, 255, 255) 25%, rgba(255, 255, 255, 0.85) 65%, rgba(255, 255, 255, 0.60) 85%, rgba(255, 255, 255, 0.00) 100%)'
        }
        position="sticky"
        top={0}
        left={0}
        right={0}
        marginBottom={`-${dimensions?.height}px`}
        zIndex={30}
      >
        <Box
          width={{
            base: '100%',
            md: '80%',
          }}
        >
          <Flex
            justifyContent="center"
            alignItems="center"
            paddingX={{
              base: '20px',
              md: 0,
            }}
          >
            {currentLogo ? (
              <Logo
                color={
                  darkMode
                    ? 'white'
                    : 'black'
                }
                logoPath={currentLogo}
                size={isMobile ? 60 : 100}
              />
            ) : null}

            <Flex
              flex={1}
              justifyContent="flex-end"
            >
              {isMobile ? (
                <IconButton
                  variant="link"
                  aria-label="Open mobile menu"
                  icon={<HamburgerIcon />}
                  color={
                    darkMode
                      ? 'white'
                      : 'black'
                  }
                  size="md"
                  onClick={() =>
                    setShowMobileView(true)
                  }
                />
              ) : (
                <>
                  {links.map((link) => (
                    <NavLink
                      key={`${link.id}-${link.title}`}
                      link={link}
                      color={
                        darkMode
                          ? 'white'
                          : 'black'
                      }
                    />
                  ))}
                </>
              )}
            </Flex>
          </Flex>
        </Box>
      </Flex>

      {showMobileView && (
        <SlideFade
          in={showMobileView}
          offsetY="-100px"
        >
          <IconButton
            position="absolute"
            top={`${positionTop + 20}px`}
            right="20px"
            variant="link"
            aria-label="Close mobile menu"
            icon={
              <CloseIcon boxSize="30px" />
            }
            color="white"
            size="md"
            onClick={() =>
              setShowMobileView(false)
            }
            zIndex={400}
          />

          <Box
            position="absolute"
            top={`${positionTop}px`}
            left={0}
            width="100%"
            height={headerHeight}
            zIndex={300}
            backgroundColor="rgba(0, 0, 0, 0.85)"
          >
            <Flex
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              height="100%"
            >
              {links.map((link) => (
                <NavLink
                  key={`${link.id}-${link.title}-mobile`}
                  link={link}
                  color="white"
                  onClick={() =>
                    setShowMobileView(false)
                  }
                />
              ))}
            </Flex>
          </Box>
        </SlideFade>
      )}
    </>
  );
};

export default Header;